import { useEffect, useState } from "react";
import ModalV2 from "../../ModalV2";
import Toggle from "../../Toggle";
import Tooltip from "../../Tooltip";
import useModal from "../../../hooks/useModal";
import useSSO from "../../../hooks/useSSO";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { X, Plus, Trash2 } from "lucide-react";
import PropTypes from "prop-types";

const defaultProvider = {
  providerId: "",
  issuer: "",
  domain: "",
  clientId: "",
  clientSecret: "",
  authorizationEndpoint: "",
  tokenEndpoint: "",
  jwksEndpoint: "",
  discoveryEndpoint: "",
  scopes: "",
  pkce: true,
  mappingId: "sub",
  mappingEmail: "email",
  mappingEmailVerified: "email_verified",
  mappingName: "name",
  mappingImage: "picture",
  extraFields: [],
};

export default function UpsertSSOProviderModal({ onCreated }) {
  const { close, data } = useModal("upsert-sso-provider-modal");
  const { registerProvider } = useSSO();
  const [providerData, setProviderData] = useState(defaultProvider);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (data) {
      setProviderData({
        providerId: data.providerId || "",
        issuer: data.issuer || "",
        domain: data.domain || "",
        clientId: data.oidcConfig?.clientId || "",
        clientSecret: data.oidcConfig?.clientSecret || "",
        authorizationEndpoint: data.oidcConfig?.authorizationEndpoint || "",
        tokenEndpoint: data.oidcConfig?.tokenEndpoint || "",
        jwksEndpoint: data.oidcConfig?.jwksEndpoint || "",
        discoveryEndpoint: data.oidcConfig?.discoveryEndpoint || "",
        scopes: data.oidcConfig?.scopes?.join(", ") || "",
        pkce: data.oidcConfig?.pkce !== undefined ? data.oidcConfig.pkce : true,
        mappingId: data.oidcConfig?.mapping?.id || "sub",
        mappingEmail: data.oidcConfig?.mapping?.email || "email",
        mappingEmailVerified: data.oidcConfig?.mapping?.emailVerified || "email_verified",
        mappingName: data.oidcConfig?.mapping?.name || "name",
        mappingImage: data.oidcConfig?.mapping?.image || "picture",
        extraFields: data.oidcConfig?.mapping?.extraFields
          ? Object.entries(data.oidcConfig.mapping.extraFields).map(([key, value]) => ({ key, value }))
          : [],
      });
    } else {
      setProviderData(defaultProvider);
    }
  }, [data]);

  const updateField = (field, value) => {
    setProviderData({ ...providerData, [field]: value });
  };

  const addExtraField = () => {
    setProviderData({
      ...providerData,
      extraFields: [...providerData.extraFields, { key: "", value: "" }],
    });
  };

  const updateExtraField = (index, field, value) => {
    const updated = [...providerData.extraFields];
    updated[index] = { ...updated[index], [field]: value };
    setProviderData({ ...providerData, extraFields: updated });
  };

  const removeExtraField = (index) => {
    setProviderData({
      ...providerData,
      extraFields: providerData.extraFields.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    if (!providerData.providerId || !providerData.issuer || !providerData.domain) {
      toast.error("Provider ID, Issuer URL, and Domain are required");
      return;
    }

    const oidcConfig = {};
    if (providerData.clientId) oidcConfig.clientId = providerData.clientId;
    if (providerData.clientSecret) oidcConfig.clientSecret = providerData.clientSecret;
    if (providerData.authorizationEndpoint) oidcConfig.authorizationEndpoint = providerData.authorizationEndpoint;
    if (providerData.tokenEndpoint) oidcConfig.tokenEndpoint = providerData.tokenEndpoint;
    if (providerData.jwksEndpoint) oidcConfig.jwksEndpoint = providerData.jwksEndpoint;
    if (providerData.discoveryEndpoint) oidcConfig.discoveryEndpoint = providerData.discoveryEndpoint;
    if (providerData.scopes) {
      oidcConfig.scopes = providerData.scopes.split(",").map((s) => s.trim()).filter(Boolean);
    }
    oidcConfig.pkce = providerData.pkce;

    const mapping = {
      id: providerData.mappingId,
      email: providerData.mappingEmail,
      name: providerData.mappingName,
    };
    if (providerData.mappingEmailVerified) mapping.emailVerified = providerData.mappingEmailVerified;
    if (providerData.mappingImage) mapping.image = providerData.mappingImage;

    const extraFieldsObj = {};
    for (const field of providerData.extraFields) {
      if (field.key && field.value) extraFieldsObj[field.key] = field.value;
    }
    if (Object.keys(extraFieldsObj).length > 0) mapping.extraFields = extraFieldsObj;

    oidcConfig.mapping = mapping;

    setIsSubmitting(true);
    try {
      await registerProvider({
        providerId: providerData.providerId,
        issuer: providerData.issuer,
        domain: providerData.domain,
        oidcConfig,
      });
      toast.success(isEditing ? "SSO provider updated" : "SSO provider created");
      onCreated?.();
      close();
    } catch (error) {
      toast.error(error.message || "Failed to save SSO provider");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal";

  const isEditing = !!data?.providerId;

  return (
    <ModalV2 name="upsert-sso-provider-modal">
      <div className="p-8 border-b border-gray-300 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-2">
            <h1 className="text-white text-3xl font-black leading-tight tracking-[-0.033em]">
              {isEditing ? "Edit SSO Provider" : "Add SSO Provider"}
            </h1>
            <p className="text-blue-100 text-base font-normal">
              Configure your OIDC-based Single Sign-On provider settings.
            </p>
          </div>
          <button
            className="text-gray-400 hover:text-white transition-colors"
            onClick={() => close()}
          >
            <X />
          </button>
        </div>
      </div>

      <div className="p-8 max-h-[70vh] overflow-y-auto">
        {/* General Settings */}
        <h2 className="text-lg font-semibold text-gray-900 mb-4">General</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          <label className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="text-gray-900 text-sm font-medium">Provider ID</p>
              <Tooltip text="A unique identifier for this SSO provider">
                <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                  info
                </span>
              </Tooltip>
            </div>
            <input
              className={inputClass}
              placeholder="e.g. okta-production"
              value={providerData.providerId}
              onChange={(e) => updateField("providerId", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="text-gray-900 text-sm font-medium">Issuer URL</p>
              <Tooltip text="The issuer URL of the identity provider">
                <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                  info
                </span>
              </Tooltip>
            </div>
            <input
              className={inputClass}
              placeholder="https://idp.example.com"
              value={providerData.issuer}
              onChange={(e) => updateField("issuer", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="text-gray-900 text-sm font-medium">Domain</p>
              <Tooltip text="The email domain associated with this provider (e.g. example.com)">
                <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                  info
                </span>
              </Tooltip>
            </div>
            <input
              className={inputClass}
              placeholder="example.com"
              value={providerData.domain}
              onChange={(e) => updateField("domain", e.target.value)}
            />
          </label>
        </div>

        {/* OIDC Configuration */}
        <h2 className="text-lg font-semibold text-gray-900 mt-10 mb-4">OIDC Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Client ID</p>
            <input
              className={inputClass}
              placeholder="your-client-id"
              value={providerData.clientId}
              onChange={(e) => updateField("clientId", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Client Secret</p>
            <input
              className={inputClass}
              type="password"
              placeholder="your-client-secret"
              value={providerData.clientSecret}
              onChange={(e) => updateField("clientSecret", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Authorization Endpoint</p>
            <input
              className={inputClass}
              placeholder="https://idp.example.com/authorize"
              value={providerData.authorizationEndpoint}
              onChange={(e) => updateField("authorizationEndpoint", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Token Endpoint</p>
            <input
              className={inputClass}
              placeholder="https://idp.example.com/token"
              value={providerData.tokenEndpoint}
              onChange={(e) => updateField("tokenEndpoint", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">JWKS Endpoint</p>
            <input
              className={inputClass}
              placeholder="https://idp.example.com/jwks"
              value={providerData.jwksEndpoint}
              onChange={(e) => updateField("jwksEndpoint", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Discovery Endpoint</p>
            <input
              className={inputClass}
              placeholder="https://idp.example.com/.well-known/openid-configuration"
              value={providerData.discoveryEndpoint}
              onChange={(e) => updateField("discoveryEndpoint", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <p className="text-gray-900 text-sm font-medium">Scopes</p>
              <Tooltip text="Comma-separated list of OIDC scopes">
                <span className="material-symbols-outlined text-xs text-gray-500 cursor-help">
                  info
                </span>
              </Tooltip>
            </div>
            <input
              className={inputClass}
              placeholder="openid, email, profile"
              value={providerData.scopes}
              onChange={(e) => updateField("scopes", e.target.value)}
            />
          </label>
        </div>

        {/* PKCE Toggle */}
        <div className="mt-6">
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-300 max-w-md">
            <div className="flex flex-col">
              <span className="text-gray-900 font-medium">PKCE</span>
              <span className="text-gray-600 text-xs">
                Enable Proof Key for Code Exchange
              </span>
            </div>
            <Toggle
              checked={providerData.pkce}
              onChange={(value) => updateField("pkce", value)}
              text="PKCE"
            />
          </div>
        </div>

        {/* Claim Mapping */}
        <h2 className="text-lg font-semibold text-gray-900 mt-10 mb-4">Claim Mapping</h2>
        <p className="text-gray-500 text-sm mb-4">
          Map OIDC claims from the identity provider to user attributes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">ID Claim</p>
            <input
              className={inputClass}
              placeholder="sub"
              value={providerData.mappingId}
              onChange={(e) => updateField("mappingId", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Email Claim</p>
            <input
              className={inputClass}
              placeholder="email"
              value={providerData.mappingEmail}
              onChange={(e) => updateField("mappingEmail", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Email Verified Claim</p>
            <input
              className={inputClass}
              placeholder="email_verified"
              value={providerData.mappingEmailVerified}
              onChange={(e) => updateField("mappingEmailVerified", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Name Claim</p>
            <input
              className={inputClass}
              placeholder="name"
              value={providerData.mappingName}
              onChange={(e) => updateField("mappingName", e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-2">
            <p className="text-gray-900 text-sm font-medium">Image Claim</p>
            <input
              className={inputClass}
              placeholder="picture"
              value={providerData.mappingImage}
              onChange={(e) => updateField("mappingImage", e.target.value)}
            />
          </label>
        </div>

        {/* Extra Fields */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Extra Fields</h3>
              <p className="text-gray-500 text-xs mt-1">
                Map additional claims to custom user fields.
              </p>
            </div>
            <button
              onClick={addExtraField}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium cursor-pointer"
            >
              <Plus size={16} />
              Add Field
            </button>
          </div>
          {providerData.extraFields.length > 0 && (
            <div className="space-y-3">
              {providerData.extraFields.map((field, index) => (
                <div key={index} className="flex items-center gap-3">
                  <input
                    className={inputClass}
                    placeholder="Field name (e.g. department)"
                    value={field.key}
                    onChange={(e) => updateExtraField(index, "key", e.target.value)}
                  />
                  <input
                    className={inputClass}
                    placeholder="Claim path (e.g. department)"
                    value={field.value}
                    onChange={(e) => updateExtraField(index, "value", e.target.value)}
                  />
                  <button
                    onClick={() => removeExtraField(index)}
                    className="text-red-400 hover:text-red-600 transition-colors cursor-pointer shrink-0"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-300">
          <div>
            <Button type="tertiary" onClick={() => close()}>
              Cancel
            </Button>
          </div>
          <div>
            <Button type="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : isEditing
                  ? "Update Provider"
                  : "Create Provider"}
            </Button>
          </div>
        </div>
      </div>
    </ModalV2>
  );
}

UpsertSSOProviderModal.propTypes = {
  onCreated: PropTypes.func,
};
